import { useQuery, useMutation } from '@tanstack/react-query';
import { regulatoryReadinessClient } from '../api/typed-clients';
import { readCsrfCookie } from '../api/client';

const HEAT_MAP_KEY = 'regulatoryHeatMap';
const JURISDICTION_KEY = 'jurisdictionProfile';
const ENTITY_CONFIGS_KEY = 'entityConfigs';

/**
 * Fetch heat map data for an entity configuration.
 */
export function useHeatMap(entityConfigId: string | undefined) {
  return useQuery({
    queryKey: [HEAT_MAP_KEY, entityConfigId],
    queryFn: async () => {
      if (!entityConfigId) return null;
      const { data, error } = await regulatoryReadinessClient.GET('/api/v1/regulatory/heat-map', {
        params: { query: { entityConfigId } },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!entityConfigId,
  });
}

/**
 * Fetch jurisdiction profile by two-letter code.
 */
export function useJurisdictionProfile(jurisdictionCode: string | undefined) {
  return useQuery({
    queryKey: [JURISDICTION_KEY, jurisdictionCode],
    queryFn: async () => {
      if (!jurisdictionCode) return null;
      const { data, error } = await regulatoryReadinessClient.GET('/api/v1/regulatory/jurisdictions/{jurisdictionCode}', {
        params: { path: { jurisdictionCode } },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!jurisdictionCode,
  });
}

/**
 * Search and list entity configurations for the FI selector.
 */
export function useEntityConfigs(searchQuery: string) {
  return useQuery({
    queryKey: [ENTITY_CONFIGS_KEY, searchQuery],
    queryFn: async () => {
      const params: { query: { q?: string } } = { query: {} };
      if (searchQuery) {
        params.query.q = searchQuery;
      }
      const { data, error } = await regulatoryReadinessClient.GET('/api/v1/regulatory/entity-configs', {
        params,
      });
      if (error) throw new Error((error as { message?: string }).message ?? 'Failed to fetch entity configs');
      return data;
    },
  });
}

/**
 * Download a PDF report (executive summary or readiness analysis).
 *
 * Uses raw fetch because openapi-fetch does not handle binary (PDF) responses.
 * This is the standard pattern for file downloads.
 */
export function useDownloadReport() {
  return useMutation({
    mutationFn: async ({ type, entityConfigId, jurisdictions }: {
      type: 'executive-summary' | 'readiness-analysis';
      entityConfigId: string;
      jurisdictions?: string[];
    }) => {
      const url = type === 'executive-summary'
        ? '/api/v1/regulatory/reports/executive-summary'
        : '/api/v1/regulatory/reports/readiness-analysis';

      // browser-token-storage-hardening: auth rides the HttpOnly cookie
      // (credentials:'include'); no Bearer token is read from the store.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // CSRF double-submit (N8): POST is a mutating method -- echo the
      // csrf-token cookie as X-CSRF-Token (no-op when the cookie is absent).
      const csrf = readCsrfCookie();
      if (csrf) headers['X-CSRF-Token'] = csrf;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ entityConfigId, jurisdictions }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Report generation failed');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = type === 'executive-summary'
        ? 'regulatory-readiness-executive-summary.pdf'
        : 'regulatory-readiness-analysis.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    },
  });
}
